/**
 * Pfad-/Inventar-Vertrag für Portal-Akten — ohne Fetch/PB/constants.
 * Tests: scripts/labor/portal-akten-neu-leer.test.ts
 */

export function normalizePortalAktePath(path?: string | null): string {
  return String(path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    .toLowerCase();
}

const PORTAL_AKTE_ID_IN_PATH = /(?:^|\/)akten\/([a-z0-9]{10,20})(?:\/|$)/i;

export function portalAkteIdFromNcPath(path?: string | null): string {
  const m = normalizePortalAktePath(path).match(PORTAL_AKTE_ID_IN_PATH);
  return m?.[1] ?? '';
}

/**
 * Neue Akte: kein Erbe der vorherigen/Testakte.
 * skip/upload = leer (AfterCreate setzt /Mandanten/<kanzlei>/akten/<id>/).
 * existing = nur der Ordner des bewusst gewählten Dokuments.
 */
export function ncPathForNewPortalAkte(args: {
  mode: 'skip' | 'upload' | 'existing';
  existingDocumentFolder?: string;
  previousAktePath?: string;
}): string | undefined {
  void args.previousAktePath;
  if (args.mode !== 'existing') return undefined;
  const folder = (args.existingDocumentFolder || '').trim();
  if (!folder) return undefined;
  return folder.endsWith('/') ? folder : `${folder}/`;
}

/** Ordner- oder Dokumentpfad gegen gespeicherten `nc_path` einer Akte matchen. */
export function matchesPortalAkteNcPath(
  akteNcPath?: string | null,
  folderOrDocPath?: string | null,
): boolean {
  const akteKey = normalizePortalAktePath(akteNcPath);
  const folderKey = normalizePortalAktePath(folderOrDocPath);
  if (!akteKey || !folderKey) return false;
  if (akteKey === folderKey) return true;
  if (folderKey.startsWith(`${akteKey}/`)) return true;

  const akteId = portalAkteIdFromNcPath(akteKey);
  const folderAkteId = portalAkteIdFromNcPath(folderKey);
  if (akteId && folderAkteId) return akteId === folderAkteId;
  if (akteId && !folderAkteId) return false;

  return folderKey.endsWith(`/${akteKey}`) || folderKey.includes(`/${akteKey}/`);
}

/** `nc_path` gehört einer anderen Akte — `/akten/<fremde-id>/` oder Legacy-Ordner ohne diese id. */
export function portalAkteNcPathIsForeign(akte: {
  id?: string | null;
  nc_path?: string | null;
}): boolean {
  const akteId = (akte.id || '').trim();
  if (!akteId) return false;
  const pathId = portalAkteIdFromNcPath(akte.nc_path);
  if (pathId) return pathId !== akteId;
  const path = (akte.nc_path || '').trim();
  if (!path) return false;
  // portal-documents/… ohne /akten/<diese-id>/ — typisch Titel-Erbe der Testakte.
  return true;
}

/** Eigener Ordner, sonst null — nie den geerbten Pfad einer anderen Akte. */
export function effectivePortalAkteNcPath(akte: {
  id?: string | null;
  nc_path?: string | null;
} | null | undefined): string | null {
  if (!akte?.id?.trim()) return null;
  if (portalAkteNcPathIsForeign(akte)) return null;
  const path = (akte.nc_path || '').trim();
  return path || null;
}

/**
 * Inventar einer Akte: gewählte `akte_id` sticht.
 * Pfad nur, wenn er `/akten/<diese-id>/` enthält — nie über geerbten Testakte-`nc_path`.
 */
export function portalDokumentBelongsToAkte(
  doc: { akte_id?: string | null; nextcloud_path?: string | null },
  akte: { id: string; nc_path?: string | null },
): boolean {
  const akteId = (akte.id || '').trim();
  if (!akteId) return false;
  const docAkte = (doc.akte_id || '').trim();
  if (docAkte) return docAkte === akteId;
  const docPathId = portalAkteIdFromNcPath(doc.nextcloud_path);
  if (docPathId) return docPathId === akteId;
  if (portalAkteNcPathIsForeign(akte)) return false;
  return matchesPortalAkteNcPath(akte.nc_path, doc.nextcloud_path);
}

/** OO-Tab: PB-Zeile wenn da, sonst Pfad — gleiche `akte_id`-Regel wie der Kasten. */
export function portalOpenMetaBelongsToAkte(
  meta: { nc_path?: string | null },
  proto: { akte_id?: string | null; nextcloud_path?: string | null } | undefined,
  akte: { id: string; nc_path?: string | null },
): boolean {
  if (proto) return portalDokumentBelongsToAkte(proto, akte);
  return portalDokumentBelongsToAkte({ nextcloud_path: meta.nc_path }, akte);
}

/**
 * Akte zum Ordner/Dokumentpfad: zuerst `/akten/<id>/`, nie geerbte fremde `nc_path`.
 * Leerer Pfad → null (nicht die erste Akte der Liste).
 */
export function findPortalAkteRecordForPath<T extends { id: string; nc_path?: string | null }>(
  akten: T[],
  folderOrDocPath?: string | null,
): T | null {
  const path = (folderOrDocPath || '').trim();
  if (!path) return null;
  const pathId = portalAkteIdFromNcPath(path);
  if (pathId) {
    const byId = akten.find((a) => a.id === pathId);
    if (byId) return byId;
  }
  return akten.find((a) => {
    if (portalAkteNcPathIsForeign(a)) return false;
    return matchesPortalAkteNcPath(a.nc_path, path);
  }) ?? null;
}
